<script lang="ts">
import { defineComponent } from "vue";

import instructorLedIconInfo from "@/components/instructorLedIconInfo.vue";
import ScrolledTelem from "@/components/ScrolledTelem.vue";
import VCarousel from "@/components/VueCarousel.vue";
import { retrieveCatalogItems } from "@/services/catalogService/courses";
import {
  bundles,
  challenges,
  courses,
  events,
  examVouchers,
  instructorLed,
  labs,
  paths,
  podcasts,
  practiceExams,
  tutorials,
  videos,
} from "@/services/middlewareService/recommendations";
import resultMap from "@/views/ForYou/recommendResults.json";
import routeMap from "@/views/ForYou/routeMap.json";

export default defineComponent({
  components: {
    VCarousel,
    ScrolledTelem,
    instructorLedIconInfo,
  },
  props: {
    topicsString: {
      type: String,
      default: "",
    },
    contentType: {
      type: String,
      default: "",
    },
    userOnboardingTutorial: {
      type: Array<string>,
      default: [],
    },
    status: {
      type: Object,
      default: null,
    },
    modelingLabsEnabled: {
      type: Boolean,
      default: true,
    },
    learningLabsEnabled: {
      type: Boolean,
      default: true,
    },
    gradedLabsEnabled: {
      type: Boolean,
      default: true,
    },
    standaloneLabsEnabled: {
      type: Boolean,
      default: true,
    },
    practiceLabsEnabled: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      resultMap,
      queryFetched: "",
      results: [],
      fetched: false,
      isVisible: false,
    };
  },
  computed: {
    resultTarget() {
      return this.resultMap[this.contentType as keyof typeof this.resultMap];
    },
  },
  watch: {
    topicsString(newTopicString, oldTopicString) {
      // only fetch recommendations if the topic string has changed and we haven't already fetched
      if (newTopicString == oldTopicString || !this.isVisible) return;

      this.fetched = false;
      this.results = [];
      setTimeout(() => {
        this.fetchRecommended();
      }, 2000);
    },
    isVisible() {
      if (!this.isVisible) return;
      if (this.queryFetched == this.topicsString && this.fetched) return;
      this.fetchRecommended();
    },
    userOnboardingTutorial() {
      this.fetchRecommended();
    },
  },
  mounted() {
    const refElement = this.$refs.recommendation as HTMLElement;
    // listen for the recommendation element to enter the viewport to fetch recommendations
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        this.isVisible = entries[0].isIntersecting;
      },
      {
        rootMargin: "0px 0px 0px 0px",
        threshold: 0.05,
      }
    );
    if (refElement) {
      intersectionObserver.observe(refElement);
    }
  },
  methods: {
    fillResults(params: any) {
      const _date = new Date();
      const _today =
        _date.getFullYear() +
        "-" +
        String(_date.getMonth() + 1).padStart(2, "0") +
        "-" +
        _date.getDate().toString().padStart(2, "0") +
        "T23:59:59Z";
      let filtredData = [];

      if (params.type == "webinar") {
        if (params.data) {
          filtredData = params.data?.filter(
            (result: any) => result.catalog_data.event_date > _today
          );
        }
        this.results = filtredData;
      } else {
        this.results = params.data;
      }
    },
    filterLabsByGates(data: any[]) {
      if (
        this.modelingLabsEnabled &&
        this.learningLabsEnabled &&
        this.gradedLabsEnabled &&
        this.standaloneLabsEnabled &&
        this.practiceLabsEnabled
      ) {
        return data;
      }

      const disabledLabTypes = new Set(
        [
          !this.modelingLabsEnabled && "cisco-modeling-labs",
          !this.learningLabsEnabled && "cisco-learning-labs",
          !this.gradedLabsEnabled && "graded-labs",
          !this.standaloneLabsEnabled && "standalone-labs",
          !this.practiceLabsEnabled && "ccie-practice-labs",
        ].filter(Boolean)
      );

      return data.filter((item: any) => {
        const labType = item.catalog_data?.lab_type ?? item.lab_type;
        return !labType || !disabledLabTypes.has(labType);
      });
    },
    fetchRecommendedData(type: string, data: any) {
      let filteredData = data?.filter(({ catalog_data }: any) => {
        let returnResult = false;
        if (catalog_data?.active) {
          returnResult = catalog_data.active == 1;
        }
        if (catalog_data?.title) {
          returnResult = catalog_data.title.length > 0;
        }
        if (catalog_data?.name) {
          returnResult = catalog_data.name.length > 0;
        }
        return returnResult;
      });

      if (type === "lab" && filteredData) {
        filteredData = this.filterLabsByGates(filteredData);
      }

      this.fillResults({
        type: type,
        data: filteredData,
      });
      this.fetched = true;
    },
    fetchRecommended() {
      this.queryFetched = this.topicsString;
      const serviceMap = {
        tutorial: tutorials,
        podcast: podcasts,
        course: courses,
        path: paths,
        webinar: events,
        video: videos,
        "instructor-led": instructorLed,
        "practice-exam": practiceExams,
        challenge: challenges,
        lab: labs,
        "exam-voucher": examVouchers,
        "exambundle-recommendations": bundles,
      };

      if (!serviceMap[this.contentType as keyof typeof serviceMap]) {
        return;
      }
      serviceMap[this.contentType as keyof typeof serviceMap]()
        .then((data) => {
          if (this.contentType == "tutorial") {
            let tutorials = this.getUserOnBordingTutorial();
            tutorials
              .then((tutorial) => {
                data = tutorial.concat(data);
                this.fetchRecommendedData(this.contentType, data);
              })
              .catch(() => {
                // do nothing
              });
            return;
          }

          let contentTypeName = this.contentType;
          if (this.contentType == "instructor-led") {
            contentTypeName = "ilt";
          } else if (this.contentType == "practice-exam") {
            contentTypeName = "pr-exam";
          } else if (this.contentType == "path") {
            contentTypeName = "learningPath";
          }

          this.fetchRecommendedData(contentTypeName, data);
        })
        .catch((error) => {
          console.log("error fetching recommendations:", error);
        });
    },
    async getUserOnBordingTutorial() {
      if (!this.userOnboardingTutorial.length || this.status?.loginCount > 5)
        return [];
      let tutorial = await retrieveCatalogItems(
        this.userOnboardingTutorial,
        "tutorials"
      );
      return tutorial.map((item: any) => {
        return {
          CourseID: item.guid,
          CourseTitle: item.title,
          CourseType: "tutorial",
          catalog_data: {
            showCTA: true,
            ...item,
          },
        };
      });
    },
    exploreType(type: string) {
      const target = routeMap[type as keyof typeof routeMap];
      if (!target) {
        return {};
      }

      if (typeof target === "string") {
        return { name: target };
      }

      const routeTarget = target as Record<string, any>;
      const routeParams = routeTarget.params;

      return {
        ...routeTarget,
        ...(routeParams ? { params: { ...routeParams } } : {}),
      };
    },
    hideNoRecommendationsSection(): boolean {
      return this.results?.length <= 0 && this.contentType == "challenge";
    },
    getViewAllText() {
      switch (this.contentType) {
        case "instructor-led":
          return `View ${this.resultTarget.title}`;
        case "lab":
        case "challenge":
        case "practice-exam":
        case "exam-voucher":
        case "exambundle-recommendations":
          return "View all in Cisco U. Store";
        case "path":
          return `View all ${this.resultTarget?.title}`;
        default:
          return `View all ${this.resultTarget?.lower}`;
      }
    },
  },
});
</script>

<template>
  <div ref="recommendation" v-if="resultTarget && resultTarget.title">
    <scrolled-telem
      v-show="contentType.length"
      :threshold="100"
      :telem-send="resultTarget?.telem"
      v-if="contentType && !hideNoRecommendationsSection()"
    >
      <VCarousel
        :resultComponent="resultTarget?.component"
        :results="results"
        :exploreAllText="resultTarget?.lower"
        :fetched="fetched"
        :topicsString="topicsString"
        :view-all-text="getViewAllText()"
        :view-all-tag="resultTarget?.lower"
        :view-all-link="exploreType(contentType)"
      >
        <div
          class="text-18 font-bold leading-[1.2] md:text-22"
          v-if="contentType == 'instructor-led'"
          data-cy="title"
        >
          <span class="block md:hidden" data-cy="small-title"
            >Instructor-Led Training</span
          >
          <instructor-led-icon-info class="flex justify-center">
            <span class="font-light">Explore&nbsp;</span
            ><span data-cy="large-title">{{ resultTarget?.title }}</span>
          </instructor-led-icon-info>
        </div>

        <div
          class="text-18 font-bold leading-[1.2] md:text-22"
          v-else
          data-cy="title"
        >
          {{ resultTarget?.title }}
        </div>
      </VCarousel>
    </scrolled-telem>
  </div>
</template>
